/**
 * Dynamic Pack Display Component
 * Replaces hardcoded 5-card layout with flexible card count support
 * Integrates with Phase 2C enhanced API responses
 */
import React, { useState, useEffect } from 'react';
import './DynamicPackDisplay.css';
import { LayoutOptimizer } from '../utils/LayoutOptimizer';
import { getGearImageUrl, handleGearImageError } from '../../utils/GearImageHelpers';
import { resolveMediaUrl } from '../../api/transport';

// Simple Card Info Popup Component (reused from SequentialPackSystem)
const CardInfoPopup = ({ card, isOpen, onClose, isMobile }) => {
    if (!isOpen || !card) return null;

    const getCardRarityColor = (rarity) => {
        switch (rarity) {
            case 'LEGENDARY': return '#FFD700'; // Gold
            case 'RARE': return '#9A4FFF'; // Purple
            case 'SILVER': return '#C0C0C0'; // Silver
            case 'BRONZE': return '#CD7F32'; // Bronze
            case 'COMMON': return '#8B8B8B'; // Gray
            default: return '#3498db'; // Blue
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                padding: '20px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    padding: '20px',
                    maxWidth: isMobile ? '280px' : '320px',
                    width: '100%',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                    position: 'relative'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'none',
                        border: 'none',
                        fontSize: '18px',
                        cursor: 'pointer',
                        color: '#666',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%'
                    }}
                >
                    ×
                </button>
                
                {/* Card Header */}
                <h4 style={{
                    margin: '0 0 15px 0',
                    color: '#2c3e50',
                    borderBottom: '2px solid #ecf0f1',
                    paddingBottom: '8px',
                    fontSize: '16px'
                }}>
                    {card.content?.name || card.content?.player_name || 'Card Info'}
                </h4>

                {/* Card Details */}
                <div>
                    {/* For Gear Cards */}
                    {card.content_type === 'gear' && (
                        <div>
                            {card.content && card.content.slot && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px',
                                    margin: '8px 0',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #9b59b6',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 6px rgba(155,89,182,0.2)'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            color: '#9b59b6'
                                        }}>
                                            Equipment Slot: {card.content.slot}
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#7f8c8d',
                                            marginTop: '2px'
                                        }}>
                                            Can be equipped to {card.content.slot} slot
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Item effect text. `player_description` (dynamic
                                legacy-transform path) and `description` (legacy
                                GeneratedPack serializer path) carry the same gear
                                effect copy — read both, never render blank. */}
                            {card.content && (card.content.player_description || card.content.description) && (
                                <div style={{
                                    padding: '12px',
                                    margin: '12px 0',
                                    backgroundColor: '#ffffff',
                                    border: '3px solid #3498db',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 8px rgba(52,152,219,0.3)'
                                }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        color: '#3498db',
                                        marginBottom: '8px'
                                    }}>
                                        Item Description
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#2c3e50',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.5'
                                    }}>
                                        {card.content.player_description || card.content.description}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* For Player Cards */}
                    {card.content_type === 'player' && card.content && (
                        <div>
                            {/* Player Position & Team */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px',
                                margin: '8px 0',
                                backgroundColor: '#ffffff',
                                border: '2px solid #3498db',
                                borderRadius: '6px',
                                boxShadow: '0 2px 6px rgba(52,152,219,0.2)'
                            }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: '#3498db'
                                    }}>
                                        Position: {card.content.position || card.type}
                                    </div>
                                    {card.content.matchup_display && (
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#7f8c8d',
                                            marginTop: '2px'
                                        }}>
                                            <span style={{ color: '#e74c3c', fontWeight: 700 }}>Matchup: </span>
                                            {card.content.matchup_display}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Projected Points */}
                            {card.content.projected_points && (
                                <div style={{
                                    padding: '10px',
                                    margin: '8px 0',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #27ae60',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 6px rgba(39,174,96,0.2)'
                                }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: '#27ae60',
                                        marginBottom: '5px'
                                    }}>
                                        Projected Fantasy Points
                                    </div>
                                    <div style={{
                                        fontSize: '16px',
                                        color: '#2c3e50',
                                        fontWeight: 'bold',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.4'
                                    }}>
                                        {Math.round(card.content.projected_points * 10) / 10} points
                                    </div>
                                </div>
                            )}

                            {/* Position Rank */}
                            {card.content.position_rank && (
                                <div style={{
                                    padding: '10px',
                                    margin: '8px 0',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #f39c12',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 6px rgba(243,156,18,0.2)'
                                }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: '#f39c12',
                                        marginBottom: '5px'
                                    }}>
                                        Position Ranking
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#636e72',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.4'
                                    }}>
                                        #{card.content.position_rank} {card.content.position}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Rarity Info */}
                    <div style={{
                        textAlign: 'center',
                        marginTop: '15px',
                        padding: '8px',
                        backgroundColor: '#ecf0f1',
                        borderRadius: '6px'
                    }}>
                        <div style={{
                            fontSize: '12px',
                            color: '#7f8c8d',
                            marginBottom: '4px'
                        }}>
                            Rarity
                        </div>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: getCardRarityColor(card.content?.rarity_tier || card.content?.rarity)
                        }}>
                            {card.content?.rarity_tier || card.content?.rarity || 'COMMON'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

class DynamicPackDisplay extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            cardCount: props.pack?.cards?.filter(card => !card.hidden)?.length || props.pack?.card_count || 5,
            layoutMode: this.determineOptimalLayout(props.pack?.cards?.filter(card => !card.hidden)?.length || props.pack?.card_count || 5),
            selectedCard: null,
            modalCard: null,
            screenDimensions: this.getScreenDimensions(),
            isModalOpen: false
        };
    }
    
    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        this.optimizeLayoutForScreen();
    }
    
    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }
    
    componentDidUpdate(prevProps) {
        // Recalculate layout when pack changes, accounting for hidden cards
        const prevVisibleCount = prevProps.pack?.cards?.filter(card => !card.hidden)?.length || 0;
        const currentVisibleCount = this.props.pack?.cards?.filter(card => !card.hidden)?.length || 0;
        
        if (prevVisibleCount !== currentVisibleCount) {
            this.setState({
                cardCount: currentVisibleCount,
                layoutMode: this.determineOptimalLayout(currentVisibleCount)
            });
        }
    }
    
    determineOptimalLayout(cardCount) {
        const { width, height } = this.state?.screenDimensions || this.getScreenDimensions();
        
        // Use enhanced layout optimizer from Phase 3 feature spec
        return LayoutOptimizer.getOptimalLayout(cardCount, width, height);
    }
    
    getScreenDimensions() {
        return {
            width: window.innerWidth,
            height: window.innerHeight
        };
    }
    
    handleResize = () => {
        const newDimensions = this.getScreenDimensions();
        const newLayoutMode = this.determineOptimalLayout(this.state.cardCount);
        
        this.setState({
            screenDimensions: newDimensions,
            layoutMode: newLayoutMode
        });
    }
    
    optimizeLayoutForScreen() {
        const { cardCount } = this.state;
        const { width } = this.state.screenDimensions;
        
        // Mobile optimization
        if (width < 768) {
            this.setState({
                layoutMode: LayoutOptimizer.optimizeForMobile(cardCount)
            });
        }
    }
    
    getCardImgSrc = (card) => {
        // Handle gear cards
        if (card.content_type === 'gear') {
            return getGearImageUrl(card.content);
        }
        
        // Handle player cards - use GSIS ID from backend data
        if (card.content_type === 'player') {
            // Prefer backend-resolved presentation URL (equipped variant or default art gateway).
            // resolveMediaUrl converts the relative /api/... path to an absolute backend URL
            // in dev (where the React dev server and Django run on different ports).
            if (card.content?.presentation?.image_url) {
                return resolveMediaUrl(card.content.presentation.image_url);
            }
            const gsisId = card.content?.gsis_id || card.content?.source_player_id;
            if (!gsisId) return '/art/mystery-item2.png';
            return `/art/player_images_gsis_cartoon/${gsisId}.png`;
        }
        
        // Fallback
        return '/art/playerImgs/ExamplePlayer.png';
    }

    normalizeDynamicCard = (card, index, packNumber) => {
        const failureContext = { card, index, packNumber };

        if (!card || typeof card !== 'object' || Array.isArray(card)) {
            console.error('[DynamicPackDisplay] INVALID DYNAMIC PACK CARD: expected a card object.', failureContext);
            return { valid: false, message: `Card ${index + 1} could not be displayed because its data was invalid.` };
        }

        if (!Number.isInteger(card.slot_number) || card.slot_number < 1) {
            console.error(
                '[DynamicPackDisplay] INVALID DYNAMIC PACK CARD: expected a positive integer slot_number.',
                failureContext
            );
            return { valid: false, message: `Card ${index + 1} could not be displayed because it has no valid slot number.` };
        }

        if (card.content_type !== 'player' && card.content_type !== 'gear') {
            console.error(
                '[DynamicPackDisplay] INVALID DYNAMIC PACK CARD: content_type must be "player" or "gear".',
                failureContext
            );
            return { valid: false, message: `Card ${card.slot_number} could not be displayed because its card type was invalid.` };
        }

        if (!card.content || typeof card.content !== 'object' || Array.isArray(card.content)) {
            console.error(
                '[DynamicPackDisplay] INVALID DYNAMIC PACK CARD: expected nested content object.',
                failureContext
            );
            return { valid: false, message: `Card ${card.slot_number} could not be displayed because its content was missing.` };
        }

        if (typeof card.content.name !== 'string' || !card.content.name.trim()) {
            console.error(
                '[DynamicPackDisplay] INVALID DYNAMIC PACK CARD: expected non-empty content.name.',
                failureContext
            );
            return { valid: false, message: `Card ${card.slot_number} could not be displayed because its name was missing.` };
        }

        if (typeof card.content.selectable !== 'boolean') {
            console.error(
                '[DynamicPackDisplay] INVALID DYNAMIC PACK CARD: expected boolean content.selectable.',
                failureContext
            );
            return { valid: false, message: `Card ${card.slot_number} could not be displayed because its selection status was missing.` };
        }

        // The active NextPack endpoint deliberately emits the legacy-compatible
        // nested card contract. Keep that as the sole wire contract and expose
        // selectability at the component boundary, where click handlers require it.
        return { valid: true, card: { ...card, selectable: card.content.selectable } };
    }

    getCardRarityBorder = (rarity) => {
        switch (rarity) {
            case 'LEGENDARY': return '3px solid #FFD700'; // Gold border
            case 'RARE': return '3px solid #9A4FFF'; // Purple border
            case 'SILVER': return '3px solid #C0C0C0'; // Silver border
            case 'BRONZE': return '3px solid #CD7F32'; // Bronze border
            case 'COMMON': return '3px solid #8B8B8B'; // Gray border
            default: return '2px solid #ddd'; // Default border
        }
    }

    extractTeamFromMatchup = (matchupDisplay) => {
        if (!matchupDisplay) return '';
        // Example: "TB vs ATL" -> return "TB"
        const parts = matchupDisplay.split(' ');
        return parts[0] || '';
    }

    extractOpponentFromMatchup = (matchupDisplay) => {
        if (!matchupDisplay) return '';
        // Example: "TB vs ATL" -> return "ATL"
        const parts = matchupDisplay.split(' vs ');
        return parts[1] || matchupDisplay;
    }
    
    renderCardGrid() {
        const { pack, onCardSelect, draftState } = this.props;
        const { layoutMode, selectedCard } = this.state;
        
        if (!pack || !Array.isArray(pack.cards)) {
            console.error('[DynamicPackDisplay] INVALID DYNAMIC PACK RESPONSE: expected pack.cards array.', {
                pack,
                packNumber: pack?.pack_number
            });
            return (
                <div className="dynamic-pack-error" role="alert">
                    We could not display this draft pack because its card data was missing. Please retry the draft screen.
                </div>
            );
        }
        
        // The active dynamic endpoint provides legacy-compatible nested cards:
        // { slot_number, type, content_type, content: { ..., selectable } }.
        // Reject malformed payloads rather than rendering blank, unclickable shells.
        const normalizedCards = pack.cards.map((card, index) =>
            this.normalizeDynamicCard(card, index, pack.pack_number)
        );
        const malformedCards = normalizedCards.filter(result => !result.valid);
        const visibleCards = normalizedCards
            .filter(result => result.valid && !result.card.hidden)
            .map(result => result.card);
        const visibleCardCount = visibleCards.length;

        if (malformedCards.length > 0) {
            console.error('[DynamicPackDisplay] REFUSING TO RENDER MALFORMED DYNAMIC PACK CARDS.', {
                packNumber: pack.pack_number,
                malformedCards,
                receivedCards: pack.cards
            });
        }
        
        return (
            <div className={`pack-display pack-layout-${layoutMode.type} card-count-${visibleCardCount}`}>
                {malformedCards.length > 0 && (
                    <div className="dynamic-pack-error" role="alert">
                        We could not display {malformedCards.length} draft {malformedCards.length === 1 ? 'card' : 'cards'}.
                        Please retry the draft screen. Details were logged for support.
                    </div>
                )}
                {visibleCards.map((card, displayIndex) => {
                    // Get original index from full cards array for proper slot identification
                    const originalIndex = pack.cards.indexOf(card);
                    const isDisabled = selectedCard && selectedCard.slot_number !== (card.slot_number || originalIndex + 1);
                    const isSelected = selectedCard && selectedCard.slot_number === (card.slot_number || originalIndex + 1);
                    const imgSrc = this.getCardImgSrc(card);

                    return (
                        <div
                            key={`card-slot-${card.slot_number || originalIndex}`}
                            className={`card-slot card-slot-${card.slot_number || originalIndex + 1} ${card.selectable ? 'selectable' : 'disabled'}`}
                            onClick={() => card.selectable && !selectedCard && this.handleCardClick(card, originalIndex)}
                            onMouseEnter={() => this.handleCardHover(card)}
                            onMouseLeave={() => this.handleCardHoverEnd()}
                            style={{
                                cursor: isDisabled ? 'not-allowed' : (card.selectable ? 'pointer' : 'default'),
                                border: isSelected ? '3px solid #27ae60' :
                                       this.getCardRarityBorder(card.content?.rarity_tier || card.content?.rarity),
                                opacity: isDisabled ? 0.5 : 1,
                                transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                boxShadow: isSelected ?
                                    '0 8px 25px rgba(39, 174, 96, 0.4)' :
                                    '0 4px 15px rgba(0,0,0,0.2)'
                            }}
                        >
                            {/* Player Image */}
                            <img
                                src={imgSrc}
                                alt={card.content?.player_name || card.content?.name || 'Card'}
                                onError={e => {
                                    if (card.content_type === 'gear') {
                                        handleGearImageError(e);
                                    } else {
                                        e.target.onerror = null;
                                        e.target.src = '/art/playerImgs/ExamplePlayer.png';
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            
                            {/* Card Content Overlay */}
                            <div className="card-overlay">
                                {/* Player/Item Name */}
                                <div className="card-name">
                                    {card.content?.player_name || card.content?.name || 'Unknown'}
                                </div>
                                
                                <div className="card-details">
                                    {/* Item effect text for Gear Cards.
                                        Gated on content_type (like .gear-rarity below), NOT
                                        card.type: when a roster position is full the backend
                                        sends type='QB' with content_type='gear', so the
                                        GEAR1/GEAR2 type check silently hid the effect box.
                                        Both field names are accepted — see normalizeDynamicCard. */}
                                    {card.content_type === 'gear' &&
                                     (card.content?.player_description || card.content?.description) && (
                                        <div className="gear-description">
                                            {card.content.player_description || card.content.description}
                                        </div>
                                    )}

                                    {/* Show rarity for gear cards only */}
                                    {card.content_type === 'gear' && (
                                        <div className="gear-rarity">
                                            {card.content?.rarity_tier || card.content?.rarity || 'LEGENDARY'}
                                        </div>
                                    )}

                                    {/* Player card details */}
                                    {card.content_type === 'player' &&
                                     (card.content?.matchup_display || card.content?.projected_points) && (
                                        <div className="player-details">
                                            {card.content?.matchup_display && (
                                                <div className="matchup-info">
                                                    <span>OPP:</span> {this.extractOpponentFromMatchup(card.content.matchup_display)}
                                                </div>
                                            )}
                                            {card.content?.projected_points && (
                                                <div className="projection-info">
                                                    <span>PROJ:</span> {Math.round(card.content.projected_points * 10) / 10}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Position/Team Box for Player Cards */}
                            {card.content_type === 'player' && (
                                <div className="position-team-box">
                                    <div>{card.content?.position || card.type}</div>
                                    <div>{this.extractTeamFromMatchup(card.content?.matchup_display)}</div>
                                </div>
                            )}

                            {/* Info Button */}
                            <button
                                className="info-button"
                                onClick={e => {
                                    e.stopPropagation();
                                    this.showCardInfo(card);
                                }}
                                title="View card details"
                            >i</button>

                            {/* Selection indicator */}
                            {isSelected && (
                                <div className="selection-indicator">
                                    ✓
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
    
    handleCardClick = (card, index) => {
        if (typeof card.selectable !== 'boolean') {
            console.error('[DynamicPackDisplay] SELECTION BLOCKED: normalized card has no boolean selectable value.', {
                card,
                packNumber: this.props.pack?.pack_number,
                index
            });
            return;
        }
        if (!card.selectable || this.state.selectedCard) return;
        
        // Prevent selection of hidden cards
        if (card.hidden) {
            console.log('Card not selectable: hidden slot');
            return;
        }
        
        const selectionData = {
            slot_number: card.slot_number || index + 1,
            card_type: card.type,
            card_data: card
        };

        this.setState({ selectedCard: selectionData });
        
        // Use enhanced selection with slot-based identification (Phase 2C integration)
        if (this.props.onCardSelect) {
            // SequentialPackSystem owns API payload construction and expects the
            // normalized card plus a stable selection ID. Passing selectionData
            // here previously left its cardId undefined, so the in-flight
            // selection lock could not reliably identify the selected slot.
            this.props.onCardSelect(card, `slot_${selectionData.slot_number}`);
        }
    }
    
    handleCardHover = (card) => {
        // Show preview information on hover
        this.setState({ hoveredCard: card });
    }
    
    handleCardHoverEnd = () => {
        this.setState({ hoveredCard: null });
    }
    
    showCardInfo = (card) => {
        this.setState({ modalCard: card, isModalOpen: true });
    }
    
    hideCardInfo = () => {
        this.setState({ modalCard: null, isModalOpen: false });
    }
    
    render() {
        const { pack, isMobile } = this.props;
        const { modalCard, isModalOpen, layoutMode } = this.state;
        
        if (!pack) {
            return (
                <div className="pack-display loading">
                    <div className="loading-spinner">Loading pack...</div>
                </div>
            );
        }
        
        return (
            <div className="dynamic-pack-container">
                <div className="pack-header">
                    <h3>{pack.pack_config?.name || 'Draft Pack'}</h3>
                    <div className="pack-info">
                        <span className="card-count">{pack.card_count} cards</span>
                        <span className={`rarity-tier ${pack.pack_config?.rarity_tier?.toLowerCase()}`}>
                            {pack.pack_config?.rarity_tier}
                        </span>
                    </div>
                </div>
                
                {this.renderCardGrid()}
                
                <CardInfoPopup 
                    card={modalCard}
                    isOpen={isModalOpen}
                    onClose={this.hideCardInfo}
                    isMobile={isMobile}
                />
                
                <div className="layout-debug" style={{ display: 'none' }}>
                    Layout: {layoutMode.type}, Cards: {pack.card_count}
                </div>
            </div>
        );
    }
}

export default DynamicPackDisplay;
